/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID,
  LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID,
  LEGACY_MSR_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_LIFETIME_PRODUCT_ID,
  LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  MSR_V6_PRODUCT_ID,
  DICTATION_PRO_V2_PRODUCT_ID,
  MSR_PRO_PRODUCT_ID,
  AUTO_FTP_V8_PRODUCT_ID,
  SECURITY_MONITOR_PRO_PRODUCT_ID,
  WEBCAM_MONITOR_PRODUCT_ID,
  QLM_PRODUCT_ID_TSL_TRIAL,
  QLM_PRODUCT_ID_TSL_45K,
  QLM_PRODUCT_ID_TSL_100K,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME,
  // DD-3 (FCR Pass 6): unified product QLM IDs (were 0/missing — now confirmed)
  QLM_PRODUCT_ID_MSR_V6,
  QLM_PRODUCT_ID_DICTATION_PRO_V2,
  QLM_PRODUCT_ID_MSR_PRO,
  QLM_PRODUCT_ID_AUTO_FTP_V8,
  QLM_PRODUCT_ID_SECURITY_MONITOR_PRO,
  QLM_PRODUCT_ID_WEBCAM_MONITOR,
} from "@/config/product-ids";

// Re-export all product IDs and helpers so existing importers of this file continue to work
// Sakshi 6: isDocumentTranslatorProduct was in cart-limits but imported from constants in use-checkout.ts
export {
  isDocumentTranslatorProduct,
  isStandaloneCartProduct,
} from "@/lib/cart-limits";

export {
  DOCUMENT_TRANSLATOR_PRODUCT_ID,
  LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID,
  LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID,
  LEGACY_MSR_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_LIFETIME_PRODUCT_ID,
  LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
  TSL_45K_PRODUCT_ID,
  TSL_EXTRA_WORDS_PRODUCT_ID,
  TSL_TRIAL_PRODUCT_ID,
  MSR_V6_PRODUCT_ID,
  DICTATION_PRO_V2_PRODUCT_ID,
  MSR_PRO_PRODUCT_ID,
  AUTO_FTP_V8_PRODUCT_ID,
  SECURITY_MONITOR_PRO_PRODUCT_ID,
  WEBCAM_MONITOR_PRODUCT_ID,
  // QLM product ID constants (F-1, S343)
  QLM_PRODUCT_ID_TSL_TRIAL,
  QLM_PRODUCT_ID_TSL_45K,
  QLM_PRODUCT_ID_TSL_100K,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL,
  QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME,
};

export const HIDEPASSWORD_DELAY = 3; // seconds

export const SUPPORT_EMAIL = "support@deskshare.com";
export const SUPPORT_MAILTO = `mailto:${SUPPORT_EMAIL}`;

// U3: Chat-first support URL — matches the centralised backend value in
// apps/notifications/templates.py. Update both locations together if the URL changes.
export const SUPPORT_CHAT_URL = "https://deskshare.com/product-support/";

export const DESKSHARE_WEBSITE_URL = "https://www.deskshare.com";

export const PRIVACY_POLICY_URL = "https://www.deskshare.com/privacy_policy.htm";

/**
 * S270 / FCR-262 v5.0 / TE-2a — Canonical app-name to display-name mapping.
 *
 * Trial.app_name in the backend stores the canonical concatenated form that
 * the desktop applications send (for example "TextSpeakerLive"). Rendering
 * that string verbatim in the UI looks unprofessional. Use displayAppName()
 * at every render site instead of the raw value.
 *
 * Keep this map in sync with backend/apps/notifications/templates.py
 * _APP_NAME_DISPLAY — both sides read from a stable canonical-name contract.
 */
export const APP_NAME_DISPLAY: Record<string, string> = {
  TextSpeakerLive: "Text Speaker Live",
  DictationPro: "Dictation Pro",
  MyScreenRecorder: "My Screen Recorder",
  AutoFTPManager: "Auto FTP Manager",
  WebCamMonitor: "WebCam Monitor",
  SecurityMonitorPro: "Security Monitor Pro",
};

/**
 * Returns the human-readable display version of a canonical app name.
 * Already-spaced or unknown values pass through unchanged so the caller
 * never produces an empty string. Falls back to "your application" when
 * the input is null/undefined/empty.
 */
export function displayAppName(appName?: string | null): string {
  if (!appName) {
    return "your application";
  }
  return APP_NAME_DISPLAY[appName] ?? appName;
}

/**
 * S270 / FCR-262 v5.0 / TE-3 — Trial verification error codes.
 *
 * The backend now returns a stable error_code on every error response from
 * /trials/verify and /trials/verify-token, so the frontend no longer has to
 * substring-match the human-readable error message. Keep these codes in
 * sync with backend/apps/trials/views.py.
 */
export const TRIAL_VERIFY_ERROR_CODE = {
  INVALID_FORMAT: "INVALID_FORMAT",
  INVALID_TOKEN: "INVALID_TOKEN",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
} as const;

export type TrialVerifyErrorCode =
  (typeof TRIAL_VERIFY_ERROR_CODE)[keyof typeof TRIAL_VERIFY_ERROR_CODE];

export const enum SOCKET_EVENTS {
  REGISTER_WINDOWS = "register_windows",
  REGISTER_WEB = "register_web",
  PAYMENT_SUCCEEDED = "payment_succeeded",
  PAYMENT_SUCCEEDED_ERROR = "payment_succeeded_error",
  PAYMENT_FAILED = "payment_failed",
  LICENSE_ACTIVATED = "license_activated",
  LICENSE_DEACTIVATE = "license-deactivated",
  PING = "ping",
  PONG = "pong",
  // I-10: Document Translator real-time events — previously missing from the enum
  UPDATE_DOCUMENT_STATUS = "update-document-status",
  TRANSLATION_ERROR = "translation-error",
  TRANSLATION_FAILED = "translation-failed",
  // S428/Change 11: DT word-credit balance update
  CREDITS_UPDATED = "credits-updated",
  // Finding 7 (S463 Pass 10): refund/dispute revoked a licence or cancelled a
  // subscription — tells an open Account Dashboard to re-fetch orders & licences.
  DASHBOARD_REFRESH = "dashboard_refresh",
}

/**
 * QLM product ID to Stripe product ID mapping (desktop-app QLM IDs → Stripe).
 * Used when the desktop sends a numeric QLM product ID and we need to resolve
 * the Stripe product for license lookup and routing.
 * Source: Backend QLM_TO_STRIPE_PRODUCT_MAPPING (apps/core/constants.py)
 *
 * S449: Keys are now derived from the canonical QLM_PRODUCT_ID_* constants
 * rather than hardcoded numeric literals. The old literals were stale and
 * wrong: Text Speaker Live was keyed under "30022"/"30023" (its real live QLM
 * ProductIDs are 25 = Basic/45K and 27 = Professional/100K, confirmed by
 * Sanjeev), and "27" was hardcoded to My Screen Recorder Lifetime — but MSR
 * Lifetime's real QLM ID is 1811, and 27 is TSL 100K. That collision meant a
 * TSL 100K desktop client sending ProductID 27 resolved to My Screen Recorder
 * Lifetime. Building the map from the constants keeps it in lockstep with the
 * backend and the env-configured IDs, so this drift cannot recur.
 */
export const stripeProductMapping: Record<string, string> = {
  [QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL]: LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME]: LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL]: LEGACY_MSR_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME]: LEGACY_MSR_LIFETIME_PRODUCT_ID,
  // Issue 10 (FCR Pass 9): one TSL entry only. Both aliases are now "22", so the
  // previous two lines collapsed to the same key and the 100K line silently
  // overwrote the 45K one -- routing every "22" lookup (e.g. the desktop app's
  // product ID over WebSocket) to the 100K product, even for 45K customers.
  // Tier routing happens separately through the Features map, not this one.
  [QLM_PRODUCT_ID_TSL_45K]: TSL_45K_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL]: LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME]: LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID,
  [QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL]: LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID,
  [QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME]: LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID,
  // Unified (SK-U) products — mirror backend QLM_TO_STRIPE_PRODUCT_MAPPING.
  [QLM_PRODUCT_ID_MSR_V6]: MSR_V6_PRODUCT_ID,
  [QLM_PRODUCT_ID_DICTATION_PRO_V2]: DICTATION_PRO_V2_PRODUCT_ID,
  [QLM_PRODUCT_ID_MSR_PRO]: MSR_PRO_PRODUCT_ID,
  [QLM_PRODUCT_ID_AUTO_FTP_V8]: AUTO_FTP_V8_PRODUCT_ID,
  [QLM_PRODUCT_ID_SECURITY_MONITOR_PRO]: SECURITY_MONITOR_PRO_PRODUCT_ID,
  [QLM_PRODUCT_ID_WEBCAM_MONITOR]: WEBCAM_MONITOR_PRODUCT_ID,
  [QLM_PRODUCT_ID_TSL_TRIAL]: TSL_TRIAL_PRODUCT_ID,
  tsl: TSL_TRIAL_PRODUCT_ID,
  dt: DOCUMENT_TRANSLATOR_PRODUCT_ID,
};

export const TSL_TRIAL_PRODUCT = TSL_TRIAL_PRODUCT_ID;

/**
 * Set of all paid TSL Stripe product IDs (excluding the trial product).
 * Used by the subscription adapter's plan-upgrade fallback and the
 * empty-card suppression filter (SK-1, SK-2 from S306 feedback).
 */
export const TSL_PAID_PRODUCTS = new Set<string>([
  TSL_45K_PRODUCT_ID,
  TSL_100K_PRODUCT_ID,
]);

/**
 * Stripe Product ID to QLM Product ID mapping.
 * Used for matching legacy licenses (those without StripeProductId)
 * to products by their QLM ProductID.
 *
 * Source: Backend QLM_TO_STRIPE_PRODUCT_MAPPING (apps/core/constants.py)
 */
export const STRIPE_TO_QLM_PRODUCT_MAP: Record<string, number> = {
  [LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID]:        Number(QLM_PRODUCT_ID_AUTO_FTP_LEGACY_ANNUAL),
  [LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID]:      Number(QLM_PRODUCT_ID_AUTO_FTP_LEGACY_LIFETIME),
  [LEGACY_MSR_ANNUAL_PRODUCT_ID]:             Number(QLM_PRODUCT_ID_MSR_LEGACY_ANNUAL),
  [LEGACY_MSR_LIFETIME_PRODUCT_ID]:           Number(QLM_PRODUCT_ID_MSR_LEGACY_LIFETIME),
  [TSL_45K_PRODUCT_ID]:                       Number(QLM_PRODUCT_ID_TSL_45K),
  [TSL_100K_PRODUCT_ID]:                      Number(QLM_PRODUCT_ID_TSL_100K),
  [LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID]:   Number(QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_ANNUAL),
  [LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID]: Number(QLM_PRODUCT_ID_DICTATION_PRO_LEGACY_LIFETIME),
  [LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID]:         Number(QLM_PRODUCT_ID_MSR_PRO_LEGACY_ANNUAL),
  [LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID]:       Number(QLM_PRODUCT_ID_MSR_PRO_LEGACY_LIFETIME),
  [TSL_TRIAL_PRODUCT_ID]:                     Number(QLM_PRODUCT_ID_TSL_TRIAL),
  // NF-2: Unified product IDs — DD-3 (FCR Pass 6): confirmed QLM ids from backend constants.py.
  // These were 0 (placeholder) which disabled ProductID matching for all six products.
  [MSR_V6_PRODUCT_ID]:               Number(QLM_PRODUCT_ID_MSR_V6),
  [DICTATION_PRO_V2_PRODUCT_ID]:     Number(QLM_PRODUCT_ID_DICTATION_PRO_V2),
  [MSR_PRO_PRODUCT_ID]:              Number(QLM_PRODUCT_ID_MSR_PRO),
  [AUTO_FTP_V8_PRODUCT_ID]:          Number(QLM_PRODUCT_ID_AUTO_FTP_V8),
  [SECURITY_MONITOR_PRO_PRODUCT_ID]: Number(QLM_PRODUCT_ID_SECURITY_MONITOR_PRO),
  [WEBCAM_MONITOR_PRODUCT_ID]:       Number(QLM_PRODUCT_ID_WEBCAM_MONITOR),
};

/**
 * Maps the stable QLM Features field value to the Stripe product ID for TSL.
 *
 * Secondary defensive layer used when StripeProductId cannot be resolved from
 * UserData1 (e.g. after an add-on purchase sets UserData1 to a cumulative total
 * like "55000" that has no static mapping). The Features field is set once at
 * license creation and is never changed by add-on purchases, making it reliable.
 *
 * Trailing-semicolon variants included for QLM server version compatibility.
 * Mirror of backend TSL_FEATURES_TO_STRIPE_PRODUCT_MAPPING in apps/core/constants.py.
 */
export const TSL_FEATURES_TO_PRODUCT_MAP: Record<string, string> = {
  // DD-2 (FCR S260): tier bit-flag format. Bit 2 = 45K, Bit 4 = 100K.
  "0:2;": TSL_45K_PRODUCT_ID,
  "0:4;": TSL_100K_PRODUCT_ID,
  "0:2":  TSL_45K_PRODUCT_ID,
  "0:4":  TSL_100K_PRODUCT_ID,
};

/**
 * Product download URLs by Stripe Product ID.
 * Synced with backend PRODUCT_DOWNLOAD_URLS (apps/core/constants.py).
 * Used by dashboard Download buttons to link users to installer pages.
 */
const PRODUCT_DOWNLOAD_URLS: Record<string, string> = {
  [LEGACY_AUTO_FTP_ANNUAL_PRODUCT_ID]:        "https://www.deskshare.com/downloads/auto-ftp",
  [LEGACY_AUTO_FTP_LIFETIME_PRODUCT_ID]:      "https://www.deskshare.com/downloads/auto-ftp",
  [LEGACY_MSR_ANNUAL_PRODUCT_ID]:             "https://www.deskshare.com/downloads/my-screen-recorder",
  [LEGACY_MSR_LIFETIME_PRODUCT_ID]:           "https://www.deskshare.com/downloads/my-screen-recorder",
  [TSL_45K_PRODUCT_ID]:                       "https://www.deskshare.com/downloads/text-speaker-live",
  [TSL_100K_PRODUCT_ID]:                      "https://www.deskshare.com/downloads/text-speaker-live",
  [LEGACY_DICTATION_PRO_ANNUAL_PRODUCT_ID]:   "https://www.deskshare.com/downloads/dictation-pro",
  [LEGACY_DICTATION_PRO_LIFETIME_PRODUCT_ID]: "https://www.deskshare.com/downloads/dictation-pro",
  [LEGACY_MSR_PRO_ANNUAL_PRODUCT_ID]:         "https://www.deskshare.com/downloads/my-screen-recorder-pro",
  [LEGACY_MSR_PRO_LIFETIME_PRODUCT_ID]:       "https://www.deskshare.com/downloads/my-screen-recorder-pro",
  [TSL_TRIAL_PRODUCT_ID]:                     "https://www.deskshare.com/downloads/text-speaker-live",
  [DOCUMENT_TRANSLATOR_PRODUCT_ID]:           "https://www.deskshare.com/downloads/document-translator",
  // SK-U unified products
  [MSR_V6_PRODUCT_ID]:               "https://www.deskshare.com/downloads/my-screen-recorder",
  [DICTATION_PRO_V2_PRODUCT_ID]:     "https://www.deskshare.com/downloads/dictation-pro",
  [MSR_PRO_PRODUCT_ID]:              "https://www.deskshare.com/downloads/my-screen-recorder-pro",
  [AUTO_FTP_V8_PRODUCT_ID]:          "https://www.deskshare.com/downloads/auto-ftp",
  [SECURITY_MONITOR_PRO_PRODUCT_ID]: "https://www.deskshare.com/downloads/security-monitor-pro",
  [WEBCAM_MONITOR_PRODUCT_ID]:       "https://www.deskshare.com/downloads/webcam-monitor",
};

export const DEFAULT_DOWNLOAD_URL = "https://www.deskshare.com/downloads";

/**
 * Get download URL for a product.
 * Falls back to generic downloads page for unknown products.
 */
export function getProductDownloadUrl(productId: string): string {
  return PRODUCT_DOWNLOAD_URLS[productId] || DEFAULT_DOWNLOAD_URL;
}

// Guardrail 2 (FCR Rev 4 §6.4): Legacy one-time product IDs that must be
// excluded from dashboard product entries. Historical invoices may still
// reference these products, but they are no longer sold.
export const DEPRECATED_LIFETIME_PRODUCT_IDS = new Set<string>([
  TSL_EXTRA_WORDS_PRODUCT_ID,
]);
